import { useState, useEffect, useRef } from 'react';
import { useLanguage } from './i18n/LanguageContext';

function useInView(options = {}) {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
      }
    }, { threshold: 0.1, ...options });

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [ref, isInView];
}

function Home() {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    enquiryType: '',
    message: ''
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState('next');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const formSteps = [
    {
      field: 'name',
      label: t.contact.steps.name,
      type: 'text',
      placeholder: t.contact.steps.namePlaceholder,
      required: true
    },
    {
      field: 'email',
      label: t.contact.steps.email,
      type: 'email',
      placeholder: t.contact.steps.emailPlaceholder,
      required: true
    },
    {
      field: 'company',
      label: t.contact.steps.company,
      type: 'text',
      placeholder: t.contact.steps.companyPlaceholder,
      required: false
    },
    {
      field: 'enquiryType',
      label: t.contact.steps.enquiryType,
      type: 'select',
      placeholder: t.contact.steps.enquiryPlaceholder,
      required: true,
      options: [
        { value: 'investor', label: t.contact.enquiryTypes.investor },
        { value: 'client', label: t.contact.enquiryTypes.client },
        { value: 'career', label: t.contact.enquiryTypes.career },
        { value: 'other', label: t.contact.enquiryTypes.other }
      ]
    },
    {
      field: 'message',
      label: t.contact.steps.message,
      type: 'textarea',
      placeholder: t.contact.steps.messagePlaceholder,
      required: true
    }
  ];

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      setIsSubmitted(true);
      setFormData({ name: '', email: '', company: '', enquiryType: '', message: '' });
      setCurrentStep(0);
    } catch (error) {
      setSubmitError('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const nextStep = () => {
    const currentField = formSteps[currentStep];
    if (currentField.required && !formData[currentField.field]) {
      return;
    }
    if (currentStep < formSteps.length - 1) {
      setDirection('next');
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setDirection('prev');
      setCurrentStep(currentStep - 1);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && formSteps[currentStep].type !== 'textarea') {
      e.preventDefault();
      nextStep();
    }
  };

  const [aboutRef, aboutInView] = useInView();
  const [visionRef, visionInView] = useInView();
  const [contactRef, contactInView] = useInView();

  return (
    <div className="page-content">
      {/* Hero Section */}
      <section className="relative min-h-screen overflow-hidden">
        <img src="/warehouse1.jpg" alt="Industrial Facility" className="w-full h-full object-cover absolute inset-0" />
        <div className="absolute inset-0 bg-gradient-to-br from-navy-dark/70 via-navy-primary/60 to-blue-accent/30"></div>

        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-accent/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gold-accent/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>

        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>

        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-8 w-full">
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 tracking-tight animate-fade-in-up leading-tight text-white">
                {t.home.heroTagline}
              </h1>
              <p className="text-xl md:text-2xl font-light mb-10 text-white/90 animate-fade-in-up-delay-1 leading-relaxed">
                {t.home.heroSubtitle}
              </p>
              <div className="flex flex-col sm:flex-row sm:items-start gap-4 animate-fade-in-up-delay-2">
                <a href="#contact" className="group inline-flex px-8 py-4 bg-gradient-to-r from-blue-accent to-light-blue text-white font-medium hover:shadow-2xl hover:shadow-blue-accent/30 transition-all duration-500 hover:-translate-y-1 items-center justify-center gap-2 self-start">
                  {t.home.getInTouch}
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </a>
                <a href="#about" className="inline-flex px-8 py-4 border-2 border-white/30 text-white font-medium hover:bg-white hover:text-navy-primary transition-all duration-300 backdrop-blur-sm text-center self-start">
                  {t.home.learnMore}
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-gray-50 to-transparent"></div>

        <div ref={aboutRef} className={`max-w-7xl mx-auto px-8 relative transition-all duration-1000 ${aboutInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-light text-navy-primary mb-6">
              {t.home.aboutTitle} <span className="font-semibold">{t.home.aboutTitleBold}</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-accent via-light-blue to-gold-accent mx-auto mb-10 rounded-full"></div>
            <p className="text-lg leading-relaxed mb-6 text-gray-700">
              {t.home.aboutPara1}
            </p>
            <p className="text-lg leading-relaxed text-gray-600 mb-6">
              {t.home.aboutPara2}
            </p>
            <p className="text-lg leading-relaxed text-gray-600">
              {t.home.aboutPara3}
            </p>
          </div>
        </div>
      </section>

      {/* Our People Section */}
      <section id="people" className="py-24 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-gray-50 to-transparent"></div>

        <div className="max-w-7xl mx-auto px-8 relative">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-light text-navy-primary mb-6">
              {t.home.peopleTitle} <span className="font-semibold">{t.home.peopleTitleBold}</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-accent via-light-blue to-gold-accent mx-auto mb-10 rounded-full"></div>
            <p className="text-lg leading-relaxed mb-6 text-gray-700">
              {t.home.peoplePara1}
            </p>
            <p className="text-lg leading-relaxed text-gray-600">
              {t.home.peoplePara2}
            </p>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section id="vision" className="py-24 bg-gradient-to-br from-navy-dark via-navy-primary to-navy-light text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-20 w-64 h-64 bg-blue-accent/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-gold-accent/10 rounded-full blur-3xl"></div>
        </div>

        <div ref={visionRef} className={`max-w-7xl mx-auto px-8 relative transition-all duration-1000 ${visionInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light text-white mb-4">{t.home.visionTitle}</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-accent via-light-blue to-gold-accent mx-auto mb-4 rounded-full"></div>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              {t.home.visionSubtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: t.home.forInvestors,
                icon: (
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                    <line x1="12" y1="22.08" x2="12" y2="12"></line>
                  </svg>
                ),
                items: [
                  t.home.investor1,
                  t.home.investor2,
                  t.home.investor3
                ]
              },
              {
                title: t.home.forClients,
                icon: (
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                  </svg>
                ),
                items: [
                  t.home.client1,
                  t.home.client2,
                  t.home.client3
                ]
              },
              {
                title: t.home.forEmployees,
                icon: (
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                ),
                items: [
                  t.home.employee1,
                  t.home.employee2,
                  t.home.employee3
                ]
              }
            ].map((vision, idx) => (
              <div key={idx} className={`group p-8 bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:border-blue-accent/30 transition-all duration-500 hover:-translate-y-2 relative overflow-hidden ${visionInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{transitionDelay: `${idx * 100}ms`}}>
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-accent/20 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                <div className="relative">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-blue-accent group-hover:text-light-blue transition-colors duration-300">{vision.icon}</span>
                    <h3 className="font-semibold text-2xl text-white">
                      {vision.title}
                    </h3>
                  </div>
                  <ul className="space-y-4">
                    {vision.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-white/80 leading-relaxed">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-accent mt-2.5 flex-shrink-0"></span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute -top-40 -left-40 w-80 h-80 bg-blue-accent/5 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-gold-accent/5 rounded-full blur-3xl"></div>
        </div>

        <div ref={contactRef} className={`max-w-7xl mx-auto px-8 relative transition-all duration-1000 ${contactInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light text-navy-primary mb-4">{t.home.contactTitle}</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-accent via-light-blue to-gold-accent mx-auto mb-4 rounded-full"></div>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              {t.home.contactSubtitle}
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            {isSubmitted ? (
              <div className="bg-white shadow-2xl border border-gray-100 p-12 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-semibold text-navy-primary mb-4">{t.contact.thankYou}</h3>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="mt-6 px-6 py-3 bg-gradient-to-r from-navy-primary to-navy-light text-white font-medium hover:shadow-xl transition-all duration-300"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
            <>
            {/* Progress Bar */}
            <div className="mb-8">
              <div className="flex justify-between mb-2">
                {formSteps.map((_, idx) => (
                  <div
                    key={idx}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${
                      idx < currentStep
                        ? 'bg-blue-accent text-white'
                        : idx === currentStep
                          ? 'bg-navy-primary text-white scale-110'
                          : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {idx < currentStep ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      idx + 1
                    )}
                  </div>
                ))}
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-navy-primary to-blue-accent transition-all duration-500 ease-out"
                  style={{ width: `${((currentStep) / (formSteps.length - 1)) * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Step Card */}
            <div className="bg-white shadow-2xl border border-gray-100 overflow-hidden">
              {/* Card Header */}
              <div className="px-8 pt-8 pb-6 border-b border-gray-100">
                <p className="text-gray-400 text-sm font-medium mb-1">Step {currentStep + 1} of {formSteps.length}</p>
                <h3 className="text-xl font-semibold text-navy-primary">{formSteps[currentStep].label}</h3>
              </div>

              {/* Card Body */}
              <div className="p-8">
                <div
                  key={currentStep}
                  className={`transition-all duration-300 ${
                    direction === 'next' ? 'animate-slide-in-right' : 'animate-slide-in-left'
                  }`}
                >
                  {formSteps[currentStep].type === 'select' ? (
                    <div className="space-y-3">
                      {formSteps[currentStep].options.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, [formSteps[currentStep].field]: option.value });
                          }}
                          className={`w-full px-6 py-4 border-2 text-left transition-all duration-300 flex items-center justify-between group ${
                            formData[formSteps[currentStep].field] === option.value
                              ? 'border-blue-accent bg-blue-accent/5 text-navy-primary'
                              : 'border-gray-200 hover:border-blue-accent/50 text-gray-700'
                          }`}
                        >
                          <span className="font-medium">{option.label}</span>
                          {formData[formSteps[currentStep].field] === option.value && (
                            <svg className="w-5 h-5 text-blue-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : formSteps[currentStep].type === 'textarea' ? (
                    <textarea
                      name={formSteps[currentStep].field}
                      value={formData[formSteps[currentStep].field]}
                      onChange={handleChange}
                      rows="5"
                      className="w-full px-5 py-4 border-2 border-gray-200 focus:border-blue-accent focus:outline-none transition-all duration-300 resize-none text-lg focus:shadow-lg focus:shadow-blue-accent/10"
                      placeholder={formSteps[currentStep].placeholder}
                      autoFocus
                    />
                  ) : (
                    <input
                      type={formSteps[currentStep].type}
                      name={formSteps[currentStep].field}
                      value={formData[formSteps[currentStep].field]}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                      className="w-full px-5 py-4 border-2 border-gray-200 focus:border-blue-accent focus:outline-none transition-all duration-300 text-lg focus:shadow-lg focus:shadow-blue-accent/10"
                      placeholder={formSteps[currentStep].placeholder}
                      autoFocus
                    />
                  )}

                  {formSteps[currentStep].required && !formData[formSteps[currentStep].field] && (
                    <p className="mt-3 text-sm text-gray-500 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {t.contact.steps.required}
                    </p>
                  )}
                </div>

                {/* Navigation Buttons */}
                <div className="flex gap-4 mt-8">
                  {currentStep > 0 && (
                    <button
                      type="button"
                      onClick={prevStep}
                      className="px-6 py-3 border-2 border-gray-200 text-gray-600 font-medium hover:border-navy-primary hover:text-navy-primary transition-all duration-300 flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                      </svg>
                      {t.contact.steps.back}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={nextStep}
                    disabled={(formSteps[currentStep].required && !formData[formSteps[currentStep].field]) || isSubmitting}
                    className={`flex-1 px-6 py-3 font-medium transition-all duration-300 flex items-center justify-center gap-2 group ${
                      (formSteps[currentStep].required && !formData[formSteps[currentStep].field]) || isSubmitting
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-navy-primary to-navy-light text-white hover:shadow-xl hover:shadow-navy-primary/20 hover:-translate-y-0.5'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : currentStep === formSteps.length - 1 ? (
                      <>
                        {t.contact.submit}
                        <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </>
                    ) : (
                      <>
                        {t.contact.continue}
                        <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>

                {submitError && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 text-red-700 text-sm">
                    {submitError}
                  </div>
                )}
              </div>
            </div>
            </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
